import { Component, DestroyRef, OnInit } from '@angular/core';
import { Property } from '../../domain/property';
import { TaskService } from '../../services/task.service';
import { Task } from '../../domain/task';
import { MessageService } from 'primeng/api';
import { PropertyStoreService } from '../../services/stores/property-store.service';
import { WorkflowTasksStoreService } from '../../services/stores/workflow-tasks-store.service';
import { LoadingOverlayStoreService } from '../../services/stores/loading-overlay-store.service';
import { RefreshDataState } from '../../domain/refresh-data-state';
import { WorkflowType } from '../../domain/enums/workflow-type';
import { UserPermissions } from '../../domain/security/user-permissions';
import { PermissionsStoreService } from '../../services/stores/permission-store-service';
import { firstValueFrom } from 'rxjs';
import { MyUnclaimedTasksComponent } from './my-unclaimed-tasks/my-unclaimed-tasks.component';
import { MyFollowUpsComponent } from './my-follow-ups/my-follow-ups.component';
import { MyClaimedTasksComponent } from './my-claimed-tasks/my-claimed-tasks.component';
import { MyMentionsComponent } from './my-mentions/my-mentions.component';
import { TopWidgetPanelComponent } from './top-widget-panel/top-widget-panel.component';
import { CommonModule } from '@angular/common';
import { LoadingState } from '../../domain/loading-state';
import { DataStoreService } from '../../services/data-store.service';
import { PhobosTaskInstanceService } from '../../services/phobos-task-instance.service';
import { WidgetStoreService } from '../../services/stores/widget-store.service';
import { WidgetType } from '../../services/util/widget-type';
import { TaskUtils } from '../../util/task-utils';
import { ActivatedRoute, Router } from '@angular/router';
import { FollowUpStoreService } from '../../services/stores/follow-up-store.service';
import { MentionStoreService } from '../../services/stores/mention-store.service';
import { DropDownStoreService } from '../../services/stores/drop-down-store.service';
import { AiSummaryStoreService } from '../../services/stores/ai-summary-store.service';
import { ModelSummary } from '../../domain/Ai/model-summary';
import { DateTime } from 'luxon';
import { DatePropertyTimezonePipe } from '../../pipes/date-property-timezone.pipe';
import { ReportService } from '../../services/report.service';
import { ReportRequest } from '../../services/request/report-request';
import { DashboardTeamMemberResponse } from '../../services/response/dashboard-team-response';
import { DataTaskChart } from '../../domain/data-task-chart';
import { AuthService } from '@auth0/auth0-angular';
import { AiSummary } from '../../domain/Ai/ai-summary';
import { DashboardDateRange } from '../../domain/dashboard/dashboard-period';
import { DashboardPeriodSelectorComponent } from './dashboard-period-selector/dashboard-period-selector.component';
import { RiskSummaryComponent } from './risk-summary/risk-summary.component';
import { DashboardService } from '../../services/dashboard.service';
import {
   DashboardRiskReportTypeResponse,
   UserPropertyDashboardResponse
} from '../../services/response/user-property-dashboard-response';
import { AuditSummaryComponent } from './audit-summary/audit-summary.component';
import { RiskTrendComponent } from './risk-trend/risk-trend.component';
import { DashboardTasksComponent } from './dashboard-tasks/dashboard-tasks.component';
import { DashboardTeamComponent } from './dashboard-team/dashboard-team.component';
import { RevenueSummaryComponent } from './revenue-summary/revenue-summary.component';
import { AutoCompletedTasksComponent } from './auto-completed-tasks/auto-completed-tasks.component';
import { TaskActionsSummaryComponent } from './task-actions-summary/task-actions-summary.component';
import { MentionsSummaryComponent } from './mentions-summary/mentions-summary.component';
import { Mention } from '../../domain/mention';
import { RiskBreakdownComponent } from './risk-breakdown/risk-breakdown.component';
import { AutoCompletionBreakdownComponent } from './auto-completion-breakdown/auto-completion-breakdown.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type UnclaimedTaskWithAiResult = {
   task: Task;
   aiSummary?: ModelSummary | null;
};

@Component({
   selector: 'app-dashboard',
   standalone: true,
   providers: [DatePropertyTimezonePipe],
   imports: [
      CommonModule,
      TopWidgetPanelComponent,
      MyUnclaimedTasksComponent,
      MyClaimedTasksComponent,
      MyMentionsComponent,
      MyFollowUpsComponent,
      DashboardPeriodSelectorComponent,
      RiskSummaryComponent,
      AuditSummaryComponent,
      RiskTrendComponent,
      DashboardTasksComponent,
      DashboardTeamComponent,
      RevenueSummaryComponent,
      AutoCompletedTasksComponent,
      TaskActionsSummaryComponent,
      MentionsSummaryComponent,
      RiskBreakdownComponent,
      AutoCompletionBreakdownComponent
   ],
   templateUrl: './dashboard.component.html',
   styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit {
   unclaimedTasks: Task[];
   filteredTasks: Task[];
   unclaimedTasksWithAi: UnclaimedTaskWithAiResult[] = [];
   isLoadingUnclaimedTasks: boolean = false;
   isClaimingUnclaimedTasks: boolean = false;
   currentProperty: Property | null = null;
   userPermissions: UserPermissions = new UserPermissions();
   outstandingTasksCount: number = 0;
   outstandingTaskList: Task[] = [];
   followUpCount: number = 0;
   mentionCount: number = 0;
   dashboardMentions: Mention[] = [];
   dashboardData: UserPropertyDashboardResponse | null = null;

   dashboardTasks: DataTaskChart[] = [];
   dashboardTeam: DashboardTeamMemberResponse[] = [];

   isLoadingDashboardTasks = false;
   dashboardTasksLoadError = false;

   isLoadingDashboardTeam = false;
   dashboardTeamLoadError = false;

   dashboardStartDate: string | null = null;
   dashboardEndDate: string | null = null;
   dashboardPeriodLabel = 'Yesterday';

   isLoadingDashboard = false;
   dashboardLoadError = false;

   currentUserId: string | null = null;

   private latestAiSummary: AiSummary | null = null;

   activeTab: string = 'unclaimed';
   activeDashboardDrawer:
      | 'unclaimed'
      | 'mentions'
      | 'risk'
      | 'autoCompletion'
      | 'followups'
      | null = null;
   selectedRiskReport: DashboardRiskReportTypeResponse | null = null;
   selectedAutoCompletionReportTypeCode: string | null = null;

   constructor(
      private workflowTaskStore: WorkflowTasksStoreService,
      private propStore: PropertyStoreService,
      private taskService: TaskService,
      private messageService: MessageService,
      private permissionsStore: PermissionsStoreService,
      private loadingOverlayStore: LoadingOverlayStoreService,
      private dataStoreService: DataStoreService,
      private formTaskService: PhobosTaskInstanceService,
      private widgetStoreService: WidgetStoreService,
      private followUpStore: FollowUpStoreService,
      private mentionStore: MentionStoreService,
      private router: Router,
      private dropDownStore: DropDownStoreService,
      private route: ActivatedRoute,
      private aiSummaryStore: AiSummaryStoreService,
      private datePipe: DatePropertyTimezonePipe,
      private auth: AuthService,
      private dashboardService: DashboardService,
      private reportService: ReportService,
      private destroyRef: DestroyRef
   ) {
      this.unclaimedTasks = [];
      this.filteredTasks = [];
   }

   async ngOnInit(): Promise<void> {
      this.isLoadingUnclaimedTasks = true;
      this.userPermissions = await firstValueFrom(this.permissionsStore.get());

      this.route.queryParams
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe((params) => {
            const tabFromURL = params['tab'];
            const savedTab = localStorage.getItem('activeTab');
            const effectiveTab = tabFromURL || savedTab || 'unclaimed';

            if (effectiveTab === 'outstanding') {
               this.goToOutstandingTasks();
            } else {
               this.setActiveTab(effectiveTab);
            }
         });

      this.auth.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
         this.currentUserId = user?.sub ?? null;
         this.loadDashboard();
      });

      this.aiSummaryStore.summary$
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe((summary) => {
            if (!summary) return;

            this.latestAiSummary = summary;

            // try enrichment if tasks already exist
            this.tryBuildUnclaimedTasksWithAi();
         });

      this.propStore.currentProperty
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe((property) => {
            if (!property?.id) return;

            this.currentProperty = property;
            this.activeDashboardDrawer = null;

            this.loadDashboardActivity();

            this.unclaimedTasks = [];
            this.filteredTasks = [];
            this.isLoadingUnclaimedTasks = true;
            this.unclaimedTasksWithAi = [];
            this.latestAiSummary = null;

            this.dropDownStore.setDropDownState(false);
            this.isLoadingUnclaimedTasks = false;
         });

      this.workflowTaskStore.unclaimedTasks
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe((tasks) => {
            this.unclaimedTasks = tasks;
            this.filterTasks(tasks);
            this.tryBuildUnclaimedTasksWithAi();
            this.filteredTasks = [...this.filteredTasks];
         });

      this.followUpStore.followUps
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe((followUps) => {
            this.followUpCount = followUps.length;
         });

      this.mentionStore.mentions
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe((mentions) => {
            this.mentionCount = mentions.length;
            this.dashboardMentions = mentions;
         });

      // Revalidate on dashboard entry: mentions may have been added by a
      // colleague while this browser was on another route or in another session.
      this.mentionStore.refreshStore();

      this.widgetStoreService.widgets
         .pipe(takeUntilDestroyed(this.destroyRef))
         .subscribe((widgets) => {
            const outstandingWidget = widgets.find(
               (widget) => widget.type === WidgetType.ClaimedTasks
            );

            if (!outstandingWidget) {
               return;
            }

            this.outstandingTasksCount = outstandingWidget.count;
            this.outstandingTaskList = outstandingWidget.data;
         });
   }

   private buildAiLookup(summary: AiSummary): Map<number, ModelSummary> {
      const map = new Map<number, ModelSummary>();

      summary.summaryResponses?.forEach((day) => {
         day.modelSummaries?.forEach((model) => {
            if (model?.taskInstanceId != null) {
               map.set(model.taskInstanceId, model);
            }
         });
      });

      return map;
   }

   openUnclaimedTasksDialog(): void {
      this.activeDashboardDrawer = 'unclaimed';
   }

   openFollowUpsDrawer(): void {
      this.activeDashboardDrawer = 'followups';
   }

   openMentionsDrawer(): void {
      this.activeDashboardDrawer = 'mentions';
   }

   openRiskBreakdownDrawer(report: DashboardRiskReportTypeResponse): void {
      this.selectedRiskReport = report;
      this.activeDashboardDrawer = 'risk';
   }

   openAutoCompletionBreakdownDrawer(reportTypeCode: string): void {
      this.selectedAutoCompletionReportTypeCode = reportTypeCode;
      this.activeDashboardDrawer = 'autoCompletion';
   }

   closeDashboardDrawer(): void {
      this.activeDashboardDrawer = null;
      this.selectedRiskReport = null;
      this.selectedAutoCompletionReportTypeCode = null;
   }
   get dashboardPropertyTimezone(): string {
      return this.currentProperty?.timeZone ?? this.datePipe.currentPropertyTimezone;
   }

   async goToDashboardMention(mention: Mention): Promise<void> {
      if (!this.currentProperty) {
         return;
      }

      this.mentionStore.refreshStore();

      await TaskUtils.goToMention(mention, this.router, this.currentProperty);
   }

   claimAllUnclaimedTasks(): void {
      if (this.filteredTasks.length === 0) {
         return;
      }

      this.claimTasks([...this.filteredTasks]);
   }

   private tryBuildUnclaimedTasksWithAi(): void {
      if (!this.unclaimedTasks.length) return;
      if (!this.latestAiSummary) return;

      const aiLookup = this.buildAiLookup(this.latestAiSummary);

      this.unclaimedTasksWithAi = this.unclaimedTasks.map((task) => ({
         task,
         aiSummary:
            task.processId != null ? (aiLookup.get(task.processId) ?? null) : null
      }));

      // ✅ REQUIRED LOG
      console.log(
         '[Dashboard] Unclaimed Tasks enriched with AI:',
         this.unclaimedTasksWithAi
      );
   }

   claimTasks(taskList: Task[]) {
      if (this.currentProperty) {
         this.isClaimingUnclaimedTasks = true;

         // Set loading state
         const loadingState = new LoadingState();
         loadingState.UnclaimedTasks = true;
         this.loadingOverlayStore.setLoadingState(loadingState);

         const taskIds: number[] = [];

         // Identify the type and prepare for claim
         for (const task of taskList) {
            if (task.type === WorkflowType.Form && task.taskId) {
               this.formTaskService
                  .claimAssignmentInstance(task.taskId)
                  .subscribe((amount) => {
                     console.log(`Claimed ${amount} form tasks`);
                  });
            } else {
               // Different logic for Phobos-enabled properties
               if (this.currentProperty.isPhobosEnabled) {
                  if (task.taskId && task.taskId > 0) {
                     taskIds.push(task.taskId);
                  }
               } else {
                  if (task.processId && task.processId > 0) {
                     taskIds.push(task.processId);
                  }
               }
            }
         }

         // Make the API call to mark them as claimed
         this.taskService.claimTasks(taskIds, this.currentProperty.id).subscribe(
            () => {
               // Remove from frontend list
               this.filteredTasks = this.filteredTasks.filter(
                  (task) => !taskIds.includes(task.processId ?? task.taskId)
               );

               // Remove from unclaimedTask list as well
               this.unclaimedTasks = this.unclaimedTasks.filter(
                  (task) => !taskIds.includes(task.processId ?? task.taskId)
               );

               // Reset loading state
               this.isClaimingUnclaimedTasks = false;
               const loadingState = new LoadingState();
               loadingState.UnclaimedTasks = false;
               this.loadingOverlayStore.setLoadingState(loadingState);

               // Show success message
               this.messageService.add({
                  severity: 'success',
                  summary: 'Tasks Successfully Claimed',
                  detail: `${taskList.length} tasks claimed`
               });

               this.workflowTaskStore.triggerClaimedTaskRefresh();
               this.loadDashboardTeam();
            },
            (error) => {
               console.log(error);

               // Show error message
               this.messageService.add({
                  severity: 'error',
                  summary: 'Unable to claim Tasks',
                  detail: `There was an error claiming ${taskList.length} tasks`
               });

               // Reset loading
               this.isClaimingUnclaimedTasks = false;
            },
            () => {
               this.isClaimingUnclaimedTasks = false;

               // Refresh the store
               const refreshDate = new RefreshDataState();
               refreshDate.ClaimedTasks = true;
               refreshDate.UnclaimedTasks = true;
               this.dataStoreService.refreshTaskData(refreshDate);
            }
         );
      }
   }

   onDashboardPeriodChanged(range: DashboardDateRange): void {
      this.dashboardStartDate = range.startDate;
      this.dashboardEndDate = range.endDate;
      this.dashboardPeriodLabel = this.getDashboardPeriodLabel(range);

      this.loadDashboard();
   }

   private getDashboardPeriodLabel(range: DashboardDateRange): string {
      switch (range.preset) {
         case 'yesterday':
            return 'Yesterday';

         case 'last7':
            return 'Last 7 Days';

         case 'custom': {
            const start = DateTime.fromISO(range.startDate).toFormat('dd/MM/yyyy');

            const end = DateTime.fromISO(range.endDate).toFormat('dd/MM/yyyy');

            return range.startDate === range.endDate ? start : `${start} - ${end}`;
         }
      }
   }

   private loadDashboard(): void {
      if (
         !this.currentProperty?.id ||
         !this.currentUserId ||
         !this.dashboardStartDate ||
         !this.dashboardEndDate
      ) {
         return;
      }

      const propertyId = this.currentProperty.id;
      const startDate = this.dashboardStartDate;
      const endDate = this.dashboardEndDate;

      this.isLoadingDashboard = true;
      this.dashboardLoadError = false;

      this.dashboardService
         .getUserDashboard(this.currentUserId, propertyId, startDate, endDate)
         .subscribe({
            next: (response) => {
               if (
                  this.currentProperty?.id !== propertyId ||
                  this.dashboardStartDate !== startDate ||
                  this.dashboardEndDate !== endDate
               ) {
                  return;
               }

               this.dashboardData = response;
               this.isLoadingDashboard = false;
            },
            error: (error) => {
               if (
                  this.currentProperty?.id !== propertyId ||
                  this.dashboardStartDate !== startDate ||
                  this.dashboardEndDate !== endDate
               ) {
                  return;
               }

               console.error('Unable to load dashboard data', error);

               this.dashboardData = null;
               this.dashboardLoadError = true;
               this.isLoadingDashboard = false;
            }
         });
   }

   get showRiskTrend(): boolean {
      return (
         !!this.dashboardStartDate &&
         !!this.dashboardEndDate &&
         this.dashboardStartDate !== this.dashboardEndDate
      );
   }

   get selectedAutoCompletionReportTypeName(): string {
      if (!this.selectedAutoCompletionReportTypeCode) {
         return 'Auto-Completed Tasks';
      }

      return (
         this.dashboardData?.autoCompletion?.tasks.find(
            (task) =>
               task.reportTypeCode === this.selectedAutoCompletionReportTypeCode
         )?.reportTypeName ?? 'Auto-Completed Tasks'
      );
   }

   goToDashboardRiskTask(taskInstanceId: number): void {
      if (!this.currentProperty) {
         return;
      }

      const task = new Task();
      task.processId = taskInstanceId;
      task.type = WorkflowType.Form;

      TaskUtils.goToTask(task, this.router, this.currentProperty);
   }

   openDashboardRiskTaskInNewTab(taskInstanceId: number): void {
      if (!this.currentProperty) {
         return;
      }

      const task = new Task();
      task.processId = taskInstanceId;
      task.type = WorkflowType.Form;

      TaskUtils.goToTaskInNewTab(task, this.router, this.currentProperty);
   }

   goToOutstandingTasks(): void {
      if (this.outstandingTaskList.length > 0 && this.currentProperty) {
         // Navigate to the first task's detail page
         TaskUtils.goToTask(
            this.outstandingTaskList[0],
            this.router,
            this.currentProperty
         );
      } else {
         console.warn(
            'No Outstanding Tasks found to navigate to, or property is null.'
         );
      }
   }

   handleWidgetClick(widgetType: string) {
      if (widgetType === 'outstanding') {
         this.goToOutstandingTasks();
      } else {
         this.setActiveTab(widgetType);
      }
   }

   private createDashboardMtdReportRequest(): ReportRequest | null {
      if (!this.currentProperty?.id) {
         return null;
      }

      const timezone = this.datePipe.currentPropertyTimezone;
      const today = DateTime.now().setZone(timezone);

      const request = new ReportRequest();

      request.start = today.startOf('month').toFormat('yyyy-MM-dd');
      request.finish = today.toFormat('yyyy-MM-dd');

      return request;
   }

   private loadDashboardTasks(): void {
      if (!this.currentProperty?.id) {
         return;
      }

      const propertyId = this.currentProperty.id;
      const request = this.createDashboardMtdReportRequest();

      if (!request) {
         return;
      }

      this.isLoadingDashboardTasks = true;
      this.dashboardTasksLoadError = false;
      this.dashboardTasks = [];

      this.reportService.getTaskReport(propertyId, request).subscribe({
         next: (records) => {
            if (this.currentProperty?.id !== propertyId) {
               return;
            }

            this.dashboardTasks = records;
            this.isLoadingDashboardTasks = false;
         },
         error: () => {
            if (this.currentProperty?.id !== propertyId) {
               return;
            }

            this.dashboardTasks = [];
            this.dashboardTasksLoadError = true;
            this.isLoadingDashboardTasks = false;
         }
      });
   }

   private loadDashboardTeam(): void {
      if (!this.currentProperty?.id) {
         return;
      }

      const propertyId = this.currentProperty.id;

      this.isLoadingDashboardTeam = true;
      this.dashboardTeamLoadError = false;
      this.dashboardTeam = [];

      this.dashboardService.getTeamDashboard(propertyId).subscribe({
         next: (response) => {
            if (this.currentProperty?.id !== propertyId) {
               return;
            }

            this.dashboardTeam = response.members;
            this.isLoadingDashboardTeam = false;
         },
         error: () => {
            if (this.currentProperty?.id !== propertyId) {
               return;
            }

            this.dashboardTeam = [];
            this.dashboardTeamLoadError = true;
            this.isLoadingDashboardTeam = false;
         }
      });
   }

   private loadDashboardActivity(): void {
      this.loadDashboardTasks();
      this.loadDashboardTeam();
   }

   private filterTasks(tasks: Task[]): void {
      if (!this.userPermissions.forms.read) {
         this.filteredTasks = tasks.filter((f) => {
            return f.type != WorkflowType.Form;
         });
      } else {
         this.filteredTasks = tasks;
      }
   }

   private setActiveTab(tab: string): void {
      this.activeTab = tab;
      localStorage.setItem('activeTab', tab);
   }
}
